import { ScaleElement } from '@scale/base-element';

class ScaleSlider extends ScaleElement {
  #slides = [];
  #dots = [];
  #progressFill = null;
  #currentIndex = 0;
  #autoplayTimer = null;
  #autoplayStopped = false;
  #observer = null;
  #isVisible = true;

  setup() {
    const track = this.ref('track');
    if (!track) return;

    this.#slides = [...track.children];

    if (this.#slides.length <= 1) {
      const prev = this.ref('prev');
      const next = this.ref('next');
      if (prev) prev.disabled = true;
      if (next) next.disabled = true;
      return;
    }

    const prev = this.ref('prev');
    const next = this.ref('next');
    if (prev) this.on('click', () => this.#handleArrow(-1), { target: prev });
    if (next) this.on('click', () => this.#handleArrow(1), { target: next });

    this.on('scroll', () => this.debounce('scroll', () => this.#updateState(), 100), { target: track });

    this.#setupPagination();
    this.#setupAutoplay();

    // Rebuild pagination on breakpoint change (perPage may differ)
    let lastMobile = this.isMobile;
    this.mediaQuery('(min-width: 750px)', () => {
      const nowMobile = this.isMobile;
      if (nowMobile === lastMobile) return;
      lastMobile = nowMobile;
      this.#rebuildPagination();
      this.#updateState();
    });

    // Design mode: scroll to selected child block
    if (Shopify?.designMode) {
      this.onDocument('shopify:block:select', (e) => {
        const block = this.querySelector(`#shopify-block-${e.detail.blockId}`);
        if (!block) return;
        const slide = this.#slides.find(s => s === block || s.contains(block));
        if (!slide) return;
        this.#stopAutoplay();
        this.#scrollToIndex(this.#slides.indexOf(slide));
      });
    }

    this.#updateState();
  }

  teardown() {
    this.#pauseAutoplay();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#slides = [];
    this.#dots = [];
    this.#progressFill = null;
  }

  // --- Navigation ---

  #handleArrow(direction) {
    this.#scroll(direction);
    this.#stopAutoplay();
  }

  #scroll(direction) {
    const track = this.ref('track');
    const slideWidth = this.#getSlideWidth(track);
    if (!slideWidth) return;

    if (this.hasAttribute('data-rewind')) {
      const currentIndex = this.#getCurrentIndex(track, slideWidth);
      const maxIndex = this.#slides.length - Math.floor(this.#getPerPage());
      if (direction === 1 && currentIndex >= maxIndex) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (direction === -1 && currentIndex <= 0) {
        track.scrollTo({ left: maxIndex * slideWidth, behavior: 'smooth' });
        return;
      }
    }

    const isRTL = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: (isRTL ? -direction : direction) * slideWidth, behavior: 'smooth' });
  }

  #scrollToIndex(index) {
    const slide = this.#slides[index];
    if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  #getSlideWidth(track) {
    const slide = track.firstElementChild;
    if (!slide) return 0;
    return slide.offsetWidth + (parseInt(getComputedStyle(track).gap) || 0);
  }

  #getCurrentIndex(track = this.ref('track'), slideWidth = this.#getSlideWidth(track)) {
    if (!slideWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / slideWidth);
  }

  #getPerPage() {
    const attr = this.isMobile ? 'data-per-page-mobile' : 'data-per-page-desktop';
    return this.getNumber(attr, 1);
  }

  #getPageCount() {
    return Math.max(1, this.#slides.length - Math.floor(this.#getPerPage()) + 1);
  }

  // --- State ---

  #updateState() {
    const track = this.ref('track');
    if (!track) return;

    this.#currentIndex = this.#getCurrentIndex();

    const rewind = this.hasAttribute('data-rewind');
    const prev = this.ref('prev');
    const next = this.ref('next');

    if (rewind) {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
    } else {
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    this.#updatePagination();
  }

  // --- Pagination ---

  #setupPagination() {
    const container = this.ref('pagination');
    if (!container) return;

    const pageCount = this.#getPageCount();
    if (pageCount <= 1) return;

    const isDots = container.classList.contains('scale-slider__pagination--dots');
    const isProgress = container.classList.contains('scale-slider__pagination--progress');

    if (isDots) {
      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'scale-slider__dot';
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        this.on('click', () => {
          this.#scrollToIndex(i);
          this.#stopAutoplay();
        }, { target: dot });
        container.appendChild(dot);
      }
      this.#dots = [...container.querySelectorAll('.scale-slider__dot')];
    } else if (isProgress) {
      const fill = document.createElement('div');
      fill.className = 'scale-slider__progress-fill';
      container.appendChild(fill);
      this.#progressFill = fill;
    }
  }

  #updatePagination() {
    for (let i = 0; i < this.#dots.length; i++) {
      this.#dots[i].classList.toggle('active', i === this.#currentIndex);
    }

    if (this.#progressFill) {
      const pageCount = this.#getPageCount();
      const pct = pageCount > 1 ? ((this.#currentIndex + 1) / pageCount) * 100 : 100;
      this.#progressFill.style.width = `${pct}%`;
    }
  }

  #rebuildPagination() {
    const container = this.ref('pagination');
    if (!container) return;
    container.innerHTML = '';
    this.#dots = [];
    this.#progressFill = null;
    this.#setupPagination();
  }

  // --- Autoplay ---

  #setupAutoplay() {
    const speed = this.getNumber('data-autoplay', 0);
    if (!speed || this.#slides.length <= 1) return;

    this.mediaQuery('(prefers-reduced-motion: reduce)', (matches) => {
      if (matches) {
        this.#pauseAutoplay();
      } else if (!this.#autoplayStopped) {
        this.#resumeAutoplay();
      }
    });

    this.on('mouseenter', () => this.#pauseAutoplay());
    this.on('mouseleave', () => {
      if (!this.#autoplayStopped) this.#resumeAutoplay();
    });

    this.on('focusin', () => this.#pauseAutoplay());
    this.on('focusout', (e) => {
      if (!this.contains(e.relatedTarget) && !this.#autoplayStopped) this.#resumeAutoplay();
    });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#isVisible = entry.isIntersecting;
      if (entry.isIntersecting && !this.#autoplayStopped) {
        this.#resumeAutoplay();
      } else {
        this.#pauseAutoplay();
      }
    }, { threshold: 0.5 });
    this.#observer.observe(this);
  }

  #resumeAutoplay() {
    if (this.#autoplayTimer || this.#autoplayStopped || !this.#isVisible) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const speed = this.getNumber('data-autoplay', 0) * 1000;
    if (!speed) return;
    this.#autoplayTimer = setInterval(() => this.#scroll(1), speed);
  }

  #pauseAutoplay() {
    if (!this.#autoplayTimer) return;
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #stopAutoplay() {
    this.#pauseAutoplay();
    this.#autoplayStopped = true;
  }
}

if (!customElements.get('scale-slider')) {
  customElements.define('scale-slider', ScaleSlider);
}
